import type { CallScriptGraph, Category, Group } from "types";

export const NodeType = {
  STATEMENT: 1,
  QUESTION: 2,
  OPTION: 32,
  START: 64,
} as const;

const DEFAULT_EDGE_INDEX = 0;

export type NestedNode = {
  id: number;
  name: string;
  displayName: string;
  type: "statement" | "question" | "option" | "start";
  elementType?: number;
  phrases: string[];
  groupId?: number;
  groupName?: string;
  position: { x: number; y: number };
  compulsory: boolean;
  weight: number;
  children: NestedNode[];
  description: string;
  isSales: boolean;
  isCompliance: boolean;
  isVerbatim: boolean;
};

export interface FlatNode extends Omit<NestedNode, "children"> {
  parentId?: number;
  childIds: number[];
}

export type OrderedGroup = {
  id: number;
  name: string;
  category: number[];
  x: number;
  y: number;
  x_dimension: number;
  y_dimension: number;
  order: number;
};

export type OptimizedCallscriptData = {
  nestedStructure: NestedNode[];
  flatStructure: FlatNode[];
  orderedGroups: OrderedGroup[];
};

/**
 * Pre-indexed edge data structure for O(1) lookups
 */
type EdgeIndex = {
  bySource: Map<number, Array<{ target: number; index: number }>>;
  targets: Set<number>;
};

/**
 * Pre-computed group information for fast lookups
 */
type GroupLookup = {
  categoryToGroup: Map<number, { id: number; name: string }>;
  groupsById: Map<number, Group>;
};

/**
 * Builds an optimized edge index for fast traversal
 */
function buildEdgeIndex(
  edges: Array<{ source: number; target: number; index: number }>,
): EdgeIndex {
  const bySource = new Map<number, Array<{ target: number; index: number }>>();
  const targets = new Set<number>();

  for (const edge of edges) {
    if (!bySource.has(edge.source)) {
      bySource.set(edge.source, []);
    }
    bySource.get(edge.source)?.push({
      target: edge.target,
      index: edge.index,
    });
    targets.add(edge.target);
  }

  // DON'T sort here - preserve original order for group assignment

  return { bySource, targets };
}

/**
 * Builds optimized group lookup structures
 */
function buildGroupLookup(groups: Group[]): GroupLookup {
  const categoryToGroup = new Map<number, { id: number; name: string }>();
  const groupsById = new Map<number, Group>();

  for (const group of groups) {
    groupsById.set(group.id, group);
    for (const categoryId of group.category) {
      categoryToGroup.set(categoryId, { id: group.id, name: group.name });
    }
  }

  return { categoryToGroup, groupsById };
}

/**
 * Converts element type to readable type string
 */
function getNodeType(elementType: number): "statement" | "question" | "option" | "start" {
  switch (elementType) {
    case NodeType.STATEMENT:
      return "statement";
    case NodeType.QUESTION:
      return "question";
    case NodeType.OPTION:
      return "option";
    case NodeType.START:
      return "start";
    default:
      return "statement";
  }
}

/**
 * Finds root nodes (START nodes or nodes with no incoming edges)
 */
function findRootNodes(categories: Category[], edgeIndex: EdgeIndex): number[] {
  const rootNodes: number[] = [];

  for (const category of categories) {
    if (category.element_type === NodeType.START || !edgeIndex.targets.has(category.id)) {
      rootNodes.push(category.id);
    }
  }

  return rootNodes;
}

/**
 * Single-pass optimized callscript structure builder
 * Builds nested, flat, and ordered group structures simultaneously
 */
export function callscriptGraphToNested(data: CallScriptGraph): OptimizedCallscriptData {
  const categoryMap = new Map<number, Category>();
  for (const category of data.categories) {
    categoryMap.set(category.id, category);
  }
  const complianceCategories = data.categories.filter((c) => c.is_compliance).map((c) => c.id);

  const edgeIndex = buildEdgeIndex(data.graph_edges);
  const groupLookup = buildGroupLookup(data.groups);
  const rootNodes = findRootNodes(data.categories, edgeIndex);

  const seenGroups = new Set<number>();
  const orderedGroups: OrderedGroup[] = [];
  let groupOrder = 0;

  /**
   * Recursively builds nested structure while tracking groups in order
   * IMPORTANT: Must match the exact traversal order of the original implementation
   */
  function buildNestedNode(
    categoryId: number,
    visitedInPath = new Set<number>(),
    seenGroupNames = new Set<number>(),
  ): NestedNode | null {
    if (visitedInPath.has(categoryId)) {
      return null;
    }

    const category = categoryMap.get(categoryId);
    if (!category) {
      return null;
    }

    const groupInfo = groupLookup.categoryToGroup.get(categoryId);

    let shouldAssignGroupName = false;
    if (groupInfo) {
      // seenGroups tracks orderedGroups insertion order globally across all branches
      if (!seenGroups.has(groupInfo.id)) {
        seenGroups.add(groupInfo.id);

        const group = groupLookup.groupsById.get(groupInfo.id);
        const groupCategories = group?.category;
        if (groupCategories) {
          const hasComplianceCategories = complianceCategories.some((x) =>
            groupCategories.includes(x),
          );
          group.isCompliance = hasComplianceCategories;
        }
        if (group) {
          orderedGroups.push({
            ...group,
            order: groupOrder++,
          });
        }
      }

      // seenGroupNames is per-branch: each option branch can independently show its group header
      if (!seenGroupNames.has(groupInfo.id)) {
        seenGroupNames.add(groupInfo.id);
        shouldAssignGroupName = true;
      }
    }

    const newVisited = new Set(visitedInPath);
    newVisited.add(categoryId);

    const childIds = edgeIndex.bySource.get(categoryId)?.map((edge) => edge.target) || [];
    const children: NestedNode[] = [];

    for (const childId of childIds) {
      // Option nodes are mutually exclusive branches — only one is ever navigated, so each
      // gets its own snapshot of seenGroupNames to independently show its group header.
      const childIsOption = categoryMap.get(childId)?.element_type === NodeType.OPTION;
      const childSeenGroupNames = childIsOption ? new Set(seenGroupNames) : seenGroupNames;
      const childNode = buildNestedNode(childId, newVisited, childSeenGroupNames);
      if (childNode) {
        children.push(childNode);
      }
    }

    const edgeIndexByTarget = new Map(
      data.graph_edges.filter((e) => e.source === categoryId).map((e) => [e.target, e.index]),
    );
    children.sort(
      (a, b) =>
        (edgeIndexByTarget.get(a.id) ?? DEFAULT_EDGE_INDEX) -
        (edgeIndexByTarget.get(b.id) ?? DEFAULT_EDGE_INDEX),
    );

    const nestedNode: NestedNode = {
      id: category.id,
      name: category.name,
      description: category.agent_phrase || category.description || "",
      isCompliance: category.is_compliance || false,
      isVerbatim: category.is_verbatim || false,
      isSales: category.is_sales || false,
      displayName: category.displayed_name || category.name,
      type: getNodeType(category.element_type),
      elementType: category.element_type,
      phrases: category.phrases?.map((p) => p.text) || [],
      ...(groupInfo && { groupId: groupInfo.id }),
      ...(shouldAssignGroupName && groupInfo && { groupName: groupInfo.name }),
      position: { x: category.x, y: category.y },
      compulsory: category.compulsory,
      weight: category.weight,
      children,
    };

    return nestedNode;
  }

  const nestedStructure: NestedNode[] = [];
  for (const rootId of rootNodes) {
    const node = buildNestedNode(rootId);
    if (node) {
      nestedStructure.push(node);
    }
  }

  const flatNodes: FlatNode[] = [];

  function flatten(node: NestedNode, parentId?: number) {
    const { children, ...nodeWithoutChildren } = node;
    const flatNode: FlatNode = {
      ...nodeWithoutChildren,
      parentId,
      childIds: children.map((c) => c.id),
    };

    flatNodes.push(flatNode);

    for (const child of children) {
      flatten(child, node.id);
    }
  }

  for (const root of nestedStructure) {
    flatten(root);
  }

  return {
    nestedStructure,
    flatStructure: flatNodes,
    orderedGroups,
  };
}
